/**
 * Artist System — Phase C API client.
 *
 * Consumes the backend-authoritative Artist endpoints (Phase B/C). All eligibility,
 * status, CC and badge values are AUTHORITATIVE from the backend — the frontend
 * never recomputes them. Profile edits carry the SIWE session + memory-only CSRF.
 */
import { API_BASE } from "./api";
import { getCsrfToken, clearCsrfToken } from "./territory-identity-api";

export class ArtistApiError extends Error {
  code: string;
  status: number;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = "ArtistApiError";
    this.status = status;
    this.code = code;
  }
}

async function parse<T>(resp: Response): Promise<T> {
  const text = await resp.text();
  let body: unknown = null;
  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    body = null;
  }
  if (!resp.ok) {
    const env = (body ?? {}) as { code?: string; message?: string; detail?: { code?: string; message?: string } };
    const code = env.code ?? env.detail?.code ?? "UNKNOWN";
    const message = env.message ?? env.detail?.message ?? resp.statusText;
    throw new ArtistApiError(resp.status, code, message);
  }
  return body as T;
}

async function get<T>(path: string, signal?: AbortSignal): Promise<T> {
  const resp = await fetch(`${API_BASE}${path}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    signal,
  });
  return parse<T>(resp);
}

// ---- Types (subset of backend responses used by the UI) ----
export interface FirstAcquisition {
  block: number | null;
  timestamp: number | null;
  timestamp_status: string | null;
  days_since_first_acquisition: number | null;
  days_toward_artist: number | null;
  artist_min_days: number;
}
export interface StatusDetail {
  wallet: string;
  status: "USER" | "CREATOR" | "NEOARTIST" | "ARTIST";
  persisted_status: string;
  owns_land: boolean;
  is_user: boolean;
  is_creator: boolean;
  is_neoartist: boolean;
  is_artist: boolean;
  neoartist_eligible: boolean;
  artist_eligible: boolean;
  artist_eligibility_units: number;
  artist_eligibility_denominator: number;
  first_acquisition: FirstAcquisition | null;
}
export interface EligibilityView {
  eligibility_units: number;
  eligibility_denominator: number;
  eligibility_basis_points: number;
  neoartist_threshold_units: number;
  artist_threshold_units: number;
  neoartist_eligible: boolean;
  artist_eligible_by_land: boolean;
  by_tier: Record<string, { slots: number; units: number; units_per_slot: number }>;
}
export interface CapacityView {
  total_cc: number;
  used_cc: number;
  remaining_cc: number;
  sources: number;
  by_tier: Record<string, { sources: number; slots: number; total_cc: number; used_cc: number; remaining_cc: number }>;
}
export interface QuoteView {
  neoartist_eligible: boolean;
  already_neoartist_or_artist: boolean;
  currencies: string[];
  fee_configured: boolean;
  fee_state: string;
  base_fee: string | null;
  quoted_amount: string | null;
  payment_integration_state: string;
  activation_available: boolean;
}
export interface SelfProfile {
  self: true;
  wallet: string;
  status: StatusDetail["status"];
  badge: string | null;
  status_detail: StatusDetail;
  profile: { display_name: string | null; avatar_url: string | null; bio: string | null; visibility: Record<string, boolean> };
  collection: { created_by: unknown[]; owned: unknown[] };
}
export interface PublicProfile {
  wallet: string;
  status: StatusDetail["status"];
  badge: string | null;
  display_name: string | null;
  avatar_url: string | null;
  bio: string | null;
  creator_since_timestamp: number | null;
  creator_since_status: string | null;
  created_artwork_count: number;
  collection: { created_by: unknown[]; owned: unknown[] };
}

export const artistApi = {
  selfStatus: (signal?: AbortSignal) => get<StatusDetail>("/api/v1/artist/self/status", signal),
  selfEligibility: (signal?: AbortSignal) => get<EligibilityView>("/api/v1/artist/self/eligibility", signal),
  selfCapacity: (signal?: AbortSignal) => get<CapacityView>("/api/v1/artist/self/capacity", signal),
  selfQuote: (signal?: AbortSignal) => get<QuoteView>("/api/v1/artist/self/neoartist-quote", signal),
  selfProfile: (signal?: AbortSignal) => get<SelfProfile>("/api/v1/artist/self/profile", signal),
  publicProfile: (wallet: string, signal?: AbortSignal) =>
    get<PublicProfile>(`/api/v1/artist/public/${encodeURIComponent(wallet)}`, signal),

  async updateProfile(patch: Record<string, unknown>): Promise<SelfProfile> {
    const doPut = async (csrf: string) =>
      fetch(`${API_BASE}/api/v1/artist/self/profile`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json", "X-The-Grid-CSRF": csrf },
        body: JSON.stringify(patch),
      });
    let resp = await doPut(await getCsrfToken());
    if (resp.status === 403) {
      const peek = await resp.clone().json().catch(() => null);
      const code = peek?.code ?? peek?.detail?.code;
      if (code === "CSRF_INVALID" || code === "CSRF_REQUIRED") {
        clearCsrfToken();
        resp = await doPut(await getCsrfToken(true));
      }
    }
    return parse<SelfProfile>(resp);
  },
};
